/** @jsx jsx */
import { useMemo, Fragment } from 'react';
import { css, jsx } from '@emotion/core';
import { BASE_APP_URL } from 'app/developer-v3/constants';

import {
  Alert,
  AlertDescription,
  ButtonGroup,
  LegacyButton,
  Spacer,
} from '@zapier/design-system';

import { useAvroEvent } from 'app/developer-v3/hooks';
import { useQueryZapTemplates } from 'app/zap-templates/hooks';

type Props = {
  bottomSpacing?: number;
};

const buttonGroupStyle = css`
  margin-top: 20px;
  margin-bottom: 10px;
`;

function NoZapTemplatesAlert({ bottomSpacing }: Props = {}) {
  // Get zap templates
  const { data, isLoading } = useQueryZapTemplates();
  const { emitPartnerUserInteractionEvent } = useAvroEvent();
  const zapTemplates = useMemo(
    () => (data?.pages ?? []).flatMap(pageOne => pageOne.data),
    [data]
  );

  function handleCreateZapTemplatesClick() {
    emitPartnerUserInteractionEvent({
      eventAction: 'click',
      interactionGoal: 'Navigate user to Zap Templates page',
      interactionName: 'create_zap_templates_button_clicked',
    });
  }

  if (isLoading || zapTemplates.length > 0) {
    // Either:
    // 1. We're in the process of loading zap templates. Don't show alert unnecessarily.
    // 2. Zap templates exist. Do not show alert.
    return null;
  }

  return (
    <Fragment>
      <Alert>
        <AlertDescription>
          You have no Zap Templates. Please publish Zap Templates to embed
          Zapier in your product.
        </AlertDescription>
        <div css={buttonGroupStyle}>
          <ButtonGroup columnSize="minmax(100px, max-content)" justify="start">
            <LegacyButton
              color="primary"
              hasShadow={true}
              href={`${BASE_APP_URL}/zap-templates`}
              iconBefore="formAdd"
              onClick={handleCreateZapTemplatesClick}
              target="_blank"
            >
              Create Zap Templates
            </LegacyButton>
          </ButtonGroup>
        </div>
      </Alert>
      {bottomSpacing != null && <Spacer height={bottomSpacing} />}
    </Fragment>
  );
}

export default NoZapTemplatesAlert;
